import { linter } from '@codemirror/lint';
import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { generateErrorsOrDiagnostics } from '../utils/generate-errors-or-diagnostics.js';

/** @internal */
export const filterFieldLintingMessages = defineMessages({
  emptyGroupError: {
    defaultMessage: 'A group cannot be empty.',
    id: 'U4LTHt7BSgC51Yri',
    description: 'Filter field empty group error',
  },
  incompleteGroupError: {
    defaultMessage: 'A group seems to be missing a bracket.',
    id: '1TnPlwjdgpaLY1uE',
    description: 'Filter field incomplete group error',
  },
  unsupportedExplicitGroup: {
    defaultMessage:
      'Grouping statements is not supported in the basic syntax mode.',
    id: '/jHof2z8yLr/qa9U',
    description: 'Filter field unsupported group',
  },
  unsupportedPositionOperator: {
    defaultMessage: 'A position operator cannot occur in this position.',
    id: 'aUi1nca6cF4KJPSw',
    description: 'Filter field unsupported position operator',
  },
  duplicatePositionOperator: {
    defaultMessage:
      'A position operator can only occur before or after a doublequote, not both.',
    id: 'l1RC/QWjUJWHIM++',
    description: 'Filter field duplicate position operator error',
  },
  invalidPositionOperator: {
    defaultMessage:
      'Invalid combination of position operators and comparator.\nAllowed comparators: =, !=',
    id: 'nS4QPK3zNih9UAZs',
    description: 'Position operator not supported for used comparator.',
  },
  incompleteStatementError: {
    defaultMessage: 'The filter field statement is incomplete.',
    id: 'jtrx2KCeXnLeA8Kd',
    description: 'Filter field incomplete statement error',
  },
  missingKeyError: {
    defaultMessage: 'The filter key is missing.',
    id: '1yn5LJXBZlEagfkL',
    description: 'Filter field missing key error',
  },
  missingValueError: {
    defaultMessage: 'The filter value is missing.',
    id: 'kctf0+Hea33LxYTL',
    description: 'Filter field missing value error',
  },
  missingOperatorError: {
    defaultMessage: 'Missing operator.',
    id: 'ABXa8Ni5H9+ZO7E0',
    description: 'Filter field missing operator',
  },
  invalidComparatorError: {
    defaultMessage:
      'Invalid comparator. Allowed comparators are: {allowedComparators}.',
    id: 'Fik0kx8pJ4hkEZUZ',
    description: 'Filter field invalid comparator',
  },
  invalidValueError: {
    defaultMessage: 'The filter value is invalid.',
    id: 'FNr7Qz7K7FFouhDD',
    description: 'Filter field invalid value error',
  },
  invalidLogicalOperator: {
    defaultMessage: 'A logical operator cannot occur in this position.',
    id: '5Byu0D+YGc1DoTBc',
    description: 'Filter field invalid logical operator',
  },
  unsupportedLogicalOperator: {
    defaultMessage:
      'The logical `OR` operator is not supported in the basic syntax mode.',
    id: 'PkWNcYRn4iWtpXnu',
    description: 'Filter field unsupported logical operator',
  },
  invalidInclusionValue: {
    defaultMessage:
      "The 'in' operator requires values to be wrapped in brackets, separated by a comma.",
    id: 'tC4AzRIolPCwyigk',
    description: 'Filter field invalid inclusion value',
  },
  emptyInclusionValue: {
    defaultMessage: "The 'in' operator doesn't support empty lists.",
    id: 'LAcIIvdezwZIzQG+',
    description: 'Filter field empty inclusion value',
  },
  missingSpace: {
    defaultMessage: 'A space seems to be missing.',
    id: 'Q7Mgijk38uWvSnlW',
    description: 'Filter field missing space error',
  },
  invalidKey: {
    defaultMessage: 'Invalid filter key.',
    id: 'rfUQCaEOsY282TT4',
    description: 'Filter field invalid key error',
  },
  invalidWildCardOperator: {
    defaultMessage: 'This wildcard operator is not valid for this key.',
    id: 'j7ELo0pUXF1X46+R',
    description: 'Filter field invalid wildcard operator',
  },
  invalidCharacterInIdentifier: {
    defaultMessage:
      "Reserved characters aren't allowed in keys or values. Escape them by adding a backslash '\\' in front or wrapping the whole string in double quotes.",
    id: 'lLbR2C5+S97CTJ4v',
    description: 'Filter field invalid characters',
  },
});

/** @internal */
export function useFilterFieldLinting() {
  const intl = useIntl();

  return useMemo(
    () =>
      linter((view) =>
        generateErrorsOrDiagnostics(view, {
          emptyGroupError: intl.formatMessage(
            filterFieldLintingMessages.emptyGroupError,
          ),
          incompleteGroupError: intl.formatMessage(
            filterFieldLintingMessages.incompleteGroupError,
          ),
          unsupportedExplicitGroup: intl.formatMessage(
            filterFieldLintingMessages.unsupportedExplicitGroup,
          ),
          unsupportedPositionOperator: intl.formatMessage(
            filterFieldLintingMessages.unsupportedPositionOperator,
          ),
          duplicatePositionOperator: intl.formatMessage(
            filterFieldLintingMessages.duplicatePositionOperator,
          ),
          invalidPositionOperator: intl.formatMessage(
            filterFieldLintingMessages.invalidPositionOperator,
          ),
          incompleteStatementError: intl.formatMessage(
            filterFieldLintingMessages.incompleteStatementError,
          ),
          missingKeyError: intl.formatMessage(
            filterFieldLintingMessages.missingKeyError,
          ),
          missingValueError: intl.formatMessage(
            filterFieldLintingMessages.missingValueError,
          ),
          missingOperatorError: intl.formatMessage(
            filterFieldLintingMessages.missingOperatorError,
          ),
          invalidValueError: intl.formatMessage(
            filterFieldLintingMessages.invalidValueError,
          ),
          invalidLogicalOperator: intl.formatMessage(
            filterFieldLintingMessages.invalidLogicalOperator,
          ),
          unsupportedLogicalOperator: intl.formatMessage(
            filterFieldLintingMessages.unsupportedLogicalOperator,
          ),
          invalidInclusionValue: intl.formatMessage(
            filterFieldLintingMessages.invalidInclusionValue,
          ),
          emptyInclusionValue: intl.formatMessage(
            filterFieldLintingMessages.emptyInclusionValue,
          ),
          missingSpace: intl.formatMessage(
            filterFieldLintingMessages.missingSpace,
          ),
          invalidKey: intl.formatMessage(filterFieldLintingMessages.invalidKey),
          invalidWildCardOperator: intl.formatMessage(
            filterFieldLintingMessages.invalidWildCardOperator,
          ),
          invalidCharacterInIdentifier: intl.formatMessage(
            filterFieldLintingMessages.invalidCharacterInIdentifier,
          ),
          invalidComparatorError: (
            allowedComparators = '=, !=, >, >=, <, <=',
          ) =>
            intl.formatMessage(
              filterFieldLintingMessages.invalidComparatorError,
              {
                allowedComparators,
              },
            ),
        }),
      ),
    [intl],
  );
}
